import { createAsyncThunk } from "@reduxjs/toolkit";
import { helpers } from "../../../../packages/helpers";
import {
  MESSAGE_CHANNEL,
  SMALL_DOWNLOAD_MESSAGES_COUNT,
  LARGE_DOWNLOAD_MESSAGES_COUNT,
  BOTH_DIRECTIONS_DOWNLOAD_MESSAGES_COUNT,
} from "../../../../packages/constants";
import { getAccessAuthorizationHeaders } from "../../../../api";
import { authSelectors } from "../../../auth";
import {
  getAuthorListData,
  getMessagesData,
  getMessagesWithReadStatusesById,
} from "../../providers";
import { CHAT_ACTIONS } from "../../types";
import type {
  AuthorList,
  GetMessagesApiCallback,
  GetMessagesQuery,
  GetMessagesResponse,
  Message,
} from "../../types";
import type { ActionStoreParams } from "../../../../store";

const { selectUserId } = authSelectors;
const { getStringValue } = helpers.text;
const { getActionErrorText } = helpers.error;

type ActionReturn = {
  authors: AuthorList;
  messages: Array<Message>;
  lastReadMessageIndex: number;
};

type ActionParams = {
  getMessagesApi: GetMessagesApiCallback;
  chatId: string;
  messageId?: string;
};

const createActionGetMessages = (
  type: CHAT_ACTIONS,
  settings: GetMessagesQuery
) =>
  createAsyncThunk<ActionReturn, ActionParams, ActionStoreParams>(
    type,
    async ({ chatId, messageId, getMessagesApi }, { getState }) => {
      const state = getState();
      const userId = selectUserId(state);

      const defaultQuery = {
        limit: LARGE_DOWNLOAD_MESSAGES_COUNT,
        ...settings,
      };

      const query = messageId ? { messageId, ...defaultQuery } : defaultQuery;

      try {
        const headers = getAccessAuthorizationHeaders(getState());

        const resp = (await getMessagesApi(chatId, query, {
          headers,
        })) as GetMessagesResponse;

        // TODO отдельный провайдер
        const messagesData = resp?.data?.messages || [];
        const authorsData = resp?.data?.authors || [];
        const lastReadMessageId = getStringValue(resp?.data?.lastReadMessageId);
        const totalReadMessageId = getStringValue(
          resp?.data?.totalReadMessageId
        );

        // TODO Временное решение которое цепляется за порядок id и проставляет статусы сообщениям https://tracker.yandex.ru/FRONT-48
        // Изменения будут в скором времени в процессе реализации
        const messagesWithoutStatuses = getMessagesData(
          messagesData,
          MESSAGE_CHANNEL.REQUEST
        );

        const params = {
          messages: messagesWithoutStatuses,
          lastReadMessageId,
          totalReadMessageId,
          userId,
        };

        const { newMessages, newLastReadMessageIndex } =
          getMessagesWithReadStatusesById(params);

        const authors = getAuthorListData(authorsData, userId);

        return {
          authors,
          messages: newMessages,
          lastReadMessageIndex: newLastReadMessageIndex,
        };
      } catch (error) {
        const text = getActionErrorText(error);

        throw new Error(text);
      }
    }
  );

export const getMessages = createActionGetMessages(CHAT_ACTIONS.GET_MESSAGES, {
  limit: LARGE_DOWNLOAD_MESSAGES_COUNT,
});

export const getPrevMessages = createActionGetMessages(
  CHAT_ACTIONS.GET_PREV_MESSAGES,
  {
    limit: SMALL_DOWNLOAD_MESSAGES_COUNT,
    route: "before",
  }
);

export const getNextMessages = createActionGetMessages(
  CHAT_ACTIONS.GET_NEXT_MESSAGES,
  {
    limit: SMALL_DOWNLOAD_MESSAGES_COUNT,
    route: "after",
  }
);

export const getMessagesByReply = createActionGetMessages(
  CHAT_ACTIONS.GET_MESSAGES_BY_REPLY,
  {
    limit: BOTH_DIRECTIONS_DOWNLOAD_MESSAGES_COUNT,
    route: "both",
  }
);
